import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Header } from '../components/shared/Header';
import { Footer } from '../components/landing/Footer';
import { SuccessAnimation } from '../components/payments/SuccessAnimation';
import { WelcomeMessage } from '../components/payments/WelcomeMessage';
import { Button } from '../components/shared/Button';
import { subscriptionService } from '../services/subscription.service';
import { ArrowRight, BookOpen, LayoutDashboard, Mail } from 'lucide-react';
import { getPlanLabel } from '../utils/planLabels';

export const PaymentSuccessPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plan, setPlan] = useState('level_bundle');
  const [nextBillingDate, setNextBillingDate] = useState<string>('—');
  const [isLoading, setIsLoading] = useState(true);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    subscriptionService
      .getStatus()
      .then((sub) => {
        if (sub.plan) {
          setPlan(sub.plan);
        }
        if (sub.currentPeriodEnd) {
          setNextBillingDate(
            new Date(sub.currentPeriodEnd).toLocaleDateString('nl-NL', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            }),
          );
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  const planLabel = getPlanLabel(plan, t);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-green-50/30">
      <Header />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 text-center border border-gray-100">
          <SuccessAnimation />

          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {isLoading ? t('common.waiting') : 'Betaling Gelukt!'} 🎉
          </h1>

          <p className="text-xl text-gray-600 mb-2">
            Je <span className="font-semibold text-primary">{planLabel}</span>-abonnement is nu actief.
          </p>
          
          <p className="text-gray-500 mb-8">
            {sessionId ? 'Je betaling is bevestigd. Een bevestigingsmail is verzonden.' : 'Je betaling is verwerkt.'}
          </p>

          <WelcomeMessage plan={planLabel} />

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
            <Button
              variant="primary"
              size="large"
              icon={<BookOpen className="w-5 h-5" />}
              onClick={() => navigate('/try-free')}
            >
              Start Je Eerste Examen
            </Button>

            <Button
              variant="secondary"
              size="large"
              icon={<LayoutDashboard className="w-5 h-5" />}
              onClick={() => navigate('/dashboard/subscription')}
            >
              Ga Naar Je Dashboard
            </Button>
          </div>

          {/* Additional Info */}
          <div className="mt-10 pt-8 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
              <div className="flex flex-col items-center gap-2 p-4 bg-gray-50 rounded-xl">
                <Mail className="w-6 h-6 text-primary" />
                <span className="font-medium">{t('payments.success.confirmEmail')}</span>
                <span className="text-gray-500 text-center">{t('payments.success.sentToInbox')}</span>
              </div>
              <div className="flex flex-col items-center gap-2 p-4 bg-gray-50 rounded-xl">
                <ArrowRight className="w-6 h-6 text-primary" />
                <span className="font-medium">{t('payments.success.directAccess')}</span>
                <span className="text-gray-500 text-center">{t('payments.success.allFeaturesAvailable')}</span>
              </div>
              <div className="flex flex-col items-center gap-2 p-4 bg-gray-50 rounded-xl">
                <span className="text-2xl">📅</span>
                <span className="font-medium">{t('payments.success.nextPayment')}</span>
                <span className="text-gray-500 text-center">{nextBillingDate}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Support Notice */}
        <p className="text-center text-gray-500 text-sm mt-8">
          Vragen over je abonnement?{' '}
          <button 
            onClick={() => navigate('/contact')}
            className="text-primary hover:underline font-medium"
          >
            Neem contact op
          </button>
        </p>
      </main>

      <Footer />
    </div>
  );
};

export default PaymentSuccessPage;
